/**
 * FavoritesPageViewModel - Shared Favorites Page ViewModel
 *
 * Maps favorites data to PageContentFavoritesProps (ButtonProductReorderProps[]).
 * Replaces the old FavoriteItemsPageViewModel with the new reorder-based layout.
 *
 * Cross-platform implementation with dependency injection.
 */

import { useCallback, useState, useEffect } from 'react'
import type {
  IPlatformServices,
  IFavoritesPageService,
  FavoriteItem,
} from '../contracts'
import { safeGetLogger } from '@restaurant/logging'

import type {
  ButtonProductReorderProps,
  PageContentFavoritesProps,
} from '@restaurant/core'

// ---------------------------------------------------------------------------
// Pure mapping function (exported for reuse in web adapter and testing)
// ---------------------------------------------------------------------------

/**
 * Maps FavoriteItem[] to ButtonProductReorderProps[] for the reorder-style layout.
 *
 * @param favorites  - Array of FavoriteItem from the service
 * @param onProductPress - Optional callback invoked with (favoriteId, productId) on press
 */
export function mapFavoritesToProducts(
  favorites: FavoriteItem[],
  onProductPress?: (favoriteId: string, productId: string) => void,
): ButtonProductReorderProps[] {
  return favorites.map((fav) => ({
    productName: fav.productName,
    productOptions: fav.productOptions,
    showProductOptions: Boolean(fav.productOptions),
    showImageProductButton: Boolean(fav.productImageUrl),
    imageUrl: fav.productImageUrl,
    disabled: false,
    onPress: onProductPress
      ? () => onProductPress(fav.id, fav.menuItem.id)
      : undefined,
  }))
}

// ---------------------------------------------------------------------------
// Dependencies & return types
// ---------------------------------------------------------------------------

export interface FavoritesPageViewModelDependencies {
  favoritesPageService?: IFavoritesPageService
  getUserId: () => string | null
  getRestaurantStoreId: () => string | null
  onFavoritesCountChange?: (count: number) => void
}

export interface FavoritesPageViewModelReturn {
  /** Raw favorites data */
  favorites: FavoriteItem[]
  /** Loading state */
  loading: boolean
  /** Error message, if any */
  error: string | null
  /** Re-fetch favorites from the service */
  refreshFavorites: () => Promise<void>
  /** Build props for PageContentFavorites */
  getPageContentFavoritesProps: () => Partial<PageContentFavoritesProps>
}

// ---------------------------------------------------------------------------
// Factory
// ---------------------------------------------------------------------------

/**
 * Factory that returns a React hook for the Favorites page.
 *
 * Usage:
 * ```ts
 * const useFavoritesPage = createFavoritesPageViewModel(services, deps)
 * // inside a component:
 * const vm = useFavoritesPage()
 * ```
 */
export const createFavoritesPageViewModel = (
  _services: IPlatformServices,
  dependencies?: FavoritesPageViewModelDependencies,
) => {
  return (): FavoritesPageViewModelReturn => {
    // State
    const [favorites, setFavorites] = useState<FavoriteItem[]>([])
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState<string | null>(null)

    // Dependencies
    const favoritesPageService = dependencies?.favoritesPageService
    const getUserId = dependencies?.getUserId ?? (() => null)
    const getRestaurantStoreId = dependencies?.getRestaurantStoreId ?? (() => null)
    const onFavoritesCountChange = dependencies?.onFavoritesCountChange

    // Resolve IDs via injected getters
    const userId = getUserId()
    const restaurantStoreId = getRestaurantStoreId()

    // ------------------------------------------------------------------
    // Data loading
    // ------------------------------------------------------------------

    const loadFavorites = useCallback(async () => {
      safeGetLogger()?.debug('FavoritesPageVM: loadFavorites called', {
        domain: 'favorites',
        userId,
        restaurantStoreId,
      })

      if (!favoritesPageService || !userId || !restaurantStoreId) {
        safeGetLogger()?.debug('FavoritesPageVM: loadFavorites skipped, missing deps', {
          domain: 'favorites',
        })
        setLoading(false)
        return
      }

      try {
        setLoading(true)
        setError(null)

        const items = await favoritesPageService.getFavoriteItems(userId, restaurantStoreId)
        safeGetLogger()?.favorites('loaded', { count: items.length })
        setFavorites(items)
        onFavoritesCountChange?.(items.length)
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to load favorites'
        setError(errorMessage)
      } finally {
        setLoading(false)
      }
    }, [favoritesPageService, userId, restaurantStoreId, onFavoritesCountChange])

    // ------------------------------------------------------------------
    // Refresh (public)
    // ------------------------------------------------------------------

    const refreshFavorites = useCallback(async () => {
      if (!favoritesPageService || !userId || !restaurantStoreId) return

      try {
        setError(null)
        const items = await favoritesPageService.refreshFavorites(userId, restaurantStoreId)
        setFavorites(items)
        onFavoritesCountChange?.(items.length)
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to refresh favorites'
        setError(errorMessage)
      }
    }, [favoritesPageService, userId, restaurantStoreId, onFavoritesCountChange])

    // ------------------------------------------------------------------
    // Prop getter
    // ------------------------------------------------------------------

    const getPageContentFavoritesProps = useCallback((): Partial<PageContentFavoritesProps> => {
      // onProductPress is intentionally a no-op — the web adapter overrides it
      // to open the product sheet via uiStore.
      const products = mapFavoritesToProducts(favorites)

      return {
        heading: 'Favorites',
        products,
        loading,
      }
    }, [favorites, loading])

    // ------------------------------------------------------------------
    // Mount effect
    // ------------------------------------------------------------------

    useEffect(() => {
      loadFavorites()
    }, [loadFavorites])

    // ------------------------------------------------------------------
    // Return
    // ------------------------------------------------------------------

    return {
      favorites,
      loading,
      error,
      refreshFavorites,
      getPageContentFavoritesProps,
    }
  }
}
